import crypto from 'crypto';

import Image from 'next/image';
import { useMemo, useState } from 'react';

import { useAuth } from '@app/hooks/useAuth';
import { cn } from '@app/libs/utils';
import { UserInfoModel } from '@app/repositories/me.repository';

interface AvatarProps {
  className?: string;
  userInfo?: UserInfoModel;
  width?: number;
  height?: number;
}

const Avatar: React.FC<AvatarProps> = ({ className, userInfo, width = 120, height = 120 }) => {
  let { info: user } = useAuth();
  const [imageError, setImageError] = useState(false);
  if (userInfo) {
    user = userInfo;
  }

  const avatarUrl = useMemo(() => user?.avatar || user?.photoUrl || 'text', [user]);
  const avatarLabel = useMemo(() => {
    const email: string = user?.email || '';
    const name: string = user?.fullName || '';
    const base = name || email;

    return base ? base.trim().charAt(0).toUpperCase() : 'Z';
  }, [user]);

  const getGravatarUrl = (email: string) => {
    if (!email) return '/images/no-avatar.jpeg';
    const hash = crypto.createHash('md5').update(email.toLowerCase()).digest('hex');
    return `https://www.gravatar.com/avatar/${hash}?d=identicon&s=200`;
  };

  return (
    <div className={cn('text-4xl relative', className)}>
      {avatarUrl !== 'text' && avatarUrl !== 'gravatar' ? (
        <Image
          src={
            imageError
              ? '/images/no-avatar.jpeg'
              : avatarUrl === 'upload'
                ? '/images/no-avatar.jpeg'
                : avatarUrl.startsWith('/')
                  ? avatarUrl
                  : `/api/v1/images/${avatarUrl}`
          }
          alt={user?.alias || user?.fullName || user?.email || ''}
          className="absolute top-0 left-0 w-full h-full object-cover"
          width={width}
          height={height}
          onError={() => setImageError(true)}
        />
      ) : avatarUrl === 'gravatar' ? (
        <Image
          src={getGravatarUrl(user?.email)}
          alt={user?.alias || user?.fullName || user?.email || ''}
          className="absolute top-0 left-0 w-full h-full object-cover"
          width={width}
          height={height}
        />
      ) : (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-gray-500">
          {avatarLabel}
        </div>
      )}
    </div>
  );
};

export default Avatar;
